import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getClubEvents,
  getClubMarks,
  getEventDraftCards,
  getEventVariants,
} from '../lib/firebase';
import { deriveSetupDone } from '../lib/clubSetupStatus';
import PortalHeader from '../components/PortalHeader';
import PageHeader from '../components/PageHeader';

// The setup workflow, in the order a new club must follow. The order is the
// teaching tool (ra-gbrd.1); the step numbers + gating hints below make the
// required sequence discoverable so a fresh-club admin knows what to do first
// instead of landing on Marks and being silently blocked (ra-gbrd.3).
//
// `needsSeason` flags steps whose data lives *inside* a season (event), so they
// have nothing to set up until a season exists. `adminOnly` gates the card on
// club/global admin (unchanged from before). `reference` marks a step that is
// optional reference material rather than a sequential build step.
// Seasons/Fleet copy signposts the two-places class model (ra-gbrd.6).
const STEPS = [
  { to: 'seasons', title: 'Seasons', desc: <>Start here: set <em>which</em> classes race each season, plus entitlements and dates</> },
  { to: 'fleets', title: 'Fleet & Class Management', desc: <>Then configure <em>those</em> classes — flag codes, fleets, start sequences and locations</> },
  { to: 'documents', title: 'Documents', desc: 'Manage documents, linking, and document set matrix', adminOnly: true, reference: true },
  { to: 'marks', title: 'Marks & Locations', desc: 'Manage racing marks, shore stations, and committee boats', needsSeason: true },
  { to: 'course-cards', title: 'Course Cards', desc: 'Enter and manage course card sequences', needsSeason: true },
  { to: 'courses/viewer', title: 'Course Viewer', desc: 'Preview courses on an interactive map', needsSeason: true },
  { to: 'racetimes', title: 'Racetimes', desc: 'Variant editor, rotation builder, and race schedule publishing', needsSeason: true },
  { to: 'race-day', title: 'Race Day', desc: 'Live race day management — starts, course, finish, results', needsSeason: true, maturity: 'alpha' },
  { to: 'reports', title: 'Race Reports', desc: 'Browse generated post-race analysis reports and open each render', needsSeason: true, maturity: 'alpha' },
  // Not part of the setup sequence — it reports on boats, not club config — but
  // it belongs on the dashboard because until now nothing on any surface could
  // say whether Onboard data was arriving (ra-gk3h). adminOnly matches the
  // firestore.rules tiering: nmea_uplink_manifest is club-admin, not RaceOfficer.
  { to: 'uplink', title: 'Onboard Uplink', desc: 'See which boats are sending Racemarks Onboard data, and when it last arrived', adminOnly: true, reference: true, maturity: 'alpha' },
];

export default function ClubDashboard() {
  const { clubId } = useParams();
  const { isGlobalAdmin, isClubAdmin } = useAuth();

  // null = not yet known; we only show first-run guidance once we know for
  // certain there are zero seasons, so a failed/slow load never blocks the nav.
  const [seasonCount, setSeasonCount] = useState(null);

  // Done-state per step, keyed by step.to slug (ra-gbrd.12). Best-effort: a
  // failed signal just leaves that step showing its number — never blocks nav.
  const [doneMap, setDoneMap] = useState({});

  useEffect(() => {
    let cancelled = false;
    // Reset so switching clubs (the route param drives this component) never
    // shows the previous club's ticks while the new signals are still loading.
    setSeasonCount(null);
    setDoneMap({});
    (async () => {
      let events;
      try {
        events = await getClubEvents(clubId);
      } catch {
        if (!cancelled) { setSeasonCount(null); setDoneMap({}); }
        return;
      }
      if (cancelled) return;
      setSeasonCount(events.length);

      // Gather the cheap done-state signals. Each read is isolated so one
      // failure can't sink the others (or the checklist as a whole).
      const [marks, draftCardsPerSeason, variantsPerSeason] = await Promise.all([
        getClubMarks(clubId).catch(() => []),
        Promise.all(events.map((e) => getEventDraftCards(clubId, e.id).catch(() => []))),
        Promise.all(events.map((e) => getEventVariants(clubId, e.id).catch(() => []))),
      ]);
      if (cancelled) return;
      setDoneMap(deriveSetupDone({ seasons: events, marks, draftCardsPerSeason, variantsPerSeason }));
    })();
    return () => { cancelled = true; };
  }, [clubId]);

  const noSeason = seasonCount === 0;

  const visibleSteps = STEPS.filter((s) => !s.adminOnly || isGlobalAdmin || isClubAdmin);

  return (
    <>
      <PortalHeader />
      <div className="page">
        <PageHeader
          backTo={isGlobalAdmin ? '/admin' : '/'}
          backLabel={isGlobalAdmin ? 'Admin' : 'Home'}
          title={clubId}
        />

        {(isGlobalAdmin || isClubAdmin) && (
          <p className="notice">
            You have admin access to this club.
          </p>
        )}

        {/* First-run guidance: a fresh club has no season, so marks/cards/etc.
            have nothing to configure yet. Point the admin at the first step. */}
        {noSeason && (
          <div className="onboarding-banner">
            <h2>New club — start here</h2>
            <p>
              Set this club up in the numbered order below. Marks, course cards,
              courses and the race schedule all live <strong>inside a season</strong>,
              so there&rsquo;s nothing to add to them until you create one first.
            </p>
            <Link to={`/clubs/${clubId}/seasons`} className="btn btn-primary btn-lg">
              Create your first season &rarr;
            </Link>
          </div>
        )}

        {/* Cards are numbered and ordered to follow the new-club setup workflow
            so the order itself teaches what to do first (ra-gbrd.1):
            Seasons → Fleets & Classes → Documents (reference) → Marks →
            Course Cards → Courses → Course Viewer → Racetimes (publish) →
            Race Day (live) → Race Reports (post-race). Step numbers, the season
            done-state and gating hints are added by ra-gbrd.3. */}
        <nav className="dashboard-nav setup-nav">
          {visibleSteps.map((step, i) => {
            const stepNum = i + 1;
            const locked = step.needsSeason && noSeason;
            // A locked step has nothing set up yet, so never tick it (a ✓ on a
            // "create a season first" card would contradict itself).
            const done = !locked && !!doneMap[step.to];
            return (
              <Link
                key={step.to}
                to={`/clubs/${clubId}/${step.to}`}
                className={`nav-card${locked ? ' locked' : ''}`}
              >
                <span className={`step-num${done ? ' done' : ''}`}>
                  {done ? '✓' : stepNum}
                </span>
                <h2>
                  {step.title}
                  {step.maturity && (
                    <span className={`maturity-badge maturity-${step.maturity}`}>
                      {step.maturity}
                    </span>
                  )}
                </h2>
                <p>{step.desc}</p>
                {locked && (
                  <p className="step-hint">Create a season first</p>
                )}
              </Link>
            );
          })}
        </nav>
      </div>
    </>
  );
}
